import { AdminPageHeader } from "@/features/admin/components/AdminPageHeader";
import { useNav } from "@/utils/nav";
import { createFileRoute } from "@tanstack/react-router";
import {
    buildAnonymousChartModel,
    useAnonymousFormSubmissions,
    type AnonymousChartModel,
    type SubmissionPeriodGranularity,
} from "@/features/admin/api/selectAnonymousFormSubmissions";
import { ui } from "@patient-watch/ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@patient-watch/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@patient-watch/ui/tooltip";
import {
    type ColumnDef,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    type SortingState,
    useReactTable,
} from "@tanstack/react-table";
import { ArrowUpDown, Loader2, Minus, TrendingDown, TrendingUp } from "lucide-react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip } from "@patient-watch/ui/chart";
import type { ChartConfig } from "@patient-watch/ui/chart";
import { useMemo, useState } from "react";

export const Route = createFileRoute("/_app/customers/anonymous")({
    component: AnonymousFormsPage,
});

const FORM_COLORS = [
    "hsl(var(--chart-1))",
    "hsl(var(--chart-2))",
    "hsl(var(--chart-3))",
    "hsl(var(--chart-4))",
    "hsl(var(--chart-5))",
    "hsl(var(--emerald-400))",
    "hsl(var(--amber-400))",
    "hsl(var(--slate-400))",
    "hsl(142, 76%, 36%)",
    "hsl(173, 58%, 39%)",
];

const GRANULARITY_META: Record<
    SubmissionPeriodGranularity,
    { selectLabel: string; rangeDescription: string; trendCompareLabel: string }
> = {
    day: { selectLabel: "Daily", rangeDescription: "Last 60 days", trendCompareLabel: "previous day" },
    week: { selectLabel: "Weekly", rangeDescription: "Last 12 weeks", trendCompareLabel: "previous week" },
    month: { selectLabel: "Monthly", rangeDescription: "Last 12 months", trendCompareLabel: "previous month" },
};

const TOOLTIP_TOP_N = 5;
const FORM_LABEL_MAX_LENGTH = 20;

function truncateFormLabel(name: string, maxLength = FORM_LABEL_MAX_LENGTH): string {
    if (name.length <= maxLength) {
        return name;
    }
    return `${name.slice(0, maxLength)}...`;
}

function FormNameLabel({ name, className }: { name: string; className?: string }) {
    const truncated = truncateFormLabel(name);

    if (truncated === name) {
        return <span className={className}>{name}</span>;
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <button type="button" className={className ?? "text-left cursor-pointer hover:underline"}>
                    {truncated}
                </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-sm">
                <p className="text-sm">{name}</p>
            </TooltipContent>
        </Tooltip>
    );
}

function generateChartConfig(formNames: string[]): ChartConfig {
    const config: ChartConfig = {};
    formNames.forEach((formName, index) => {
        config[formName] = {
            label: truncateFormLabel(formName),
            color: FORM_COLORS[index % FORM_COLORS.length],
        };
    });
    return config;
}

interface FormTableRow {
    formName: string;
    countsByPeriod: number[];
    rowTotal: number;
}

function buildFormTableRows(model: AnonymousChartModel): FormTableRow[] {
    return model.formNames.map((formName) => {
        const countsByPeriod = model.chartRows.map((row) => Number(row[formName]) || 0);
        return {
            formName,
            countsByPeriod,
            rowTotal: countsByPeriod.reduce((sum, count) => sum + count, 0),
        };
    });
}

function SortableTableHeader({
    label,
    onClick,
    className,
}: {
    label: string;
    onClick: () => void;
    className?: string;
}) {
    return (
        <ui.Button
            type="button"
            variant="ghost"
            size="sm"
            className={className ?? "justify-end px-2 h-8 font-medium"}
            onClick={onClick}
        >
            {label}
            <ArrowUpDown className="opacity-70 ml-1 w-3.5 h-3.5 shrink-0" aria-hidden />
        </ui.Button>
    );
}

function buildSubmissionTableColumns(model: AnonymousChartModel): ColumnDef<FormTableRow>[] {
    const periodColumns: ColumnDef<FormTableRow>[] = model.periods.map((period, index) => ({
        id: `period-${period.key}`,
        accessorFn: (row) => row.countsByPeriod[index] ?? 0,
        header: ({ column }) => (
            <SortableTableHeader
                label={period.label}
                onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            />
        ),
        cell: ({ row }) => {
            const count = row.original.countsByPeriod[index] ?? 0;
            return <span className="tabular-nums">{count > 0 ? count.toLocaleString() : "-"}</span>;
        },
        meta: { align: "right" as const },
    }));

    return [
        {
            accessorKey: "formName",
            header: ({ column }) => (
                <SortableTableHeader
                    label="Questionnaire"
                    className="justify-start px-2 h-8 font-medium"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                />
            ),
            cell: ({ row }) => <FormNameLabel name={row.original.formName} className="font-medium text-left" />,
        },
        ...periodColumns,
        {
            accessorKey: "rowTotal",
            header: ({ column }) => (
                <SortableTableHeader
                    label="Total"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                />
            ),
            cell: ({ row }) => (
                <span className="font-medium tabular-nums">{row.original.rowTotal.toLocaleString()}</span>
            ),
            meta: { align: "right" as const },
        },
    ];
}

interface AnonymousChartTooltipPayloadItem {
    name?: string;
    dataKey?: string | number;
    value?: number | string;
    color?: string;
}

interface AnonymousChartTooltipProps {
    active?: boolean;
    payload?: AnonymousChartTooltipPayloadItem[];
    label?: string | number;
    chartConfig: ChartConfig;
}

function AnonymousChartTooltip({ active, payload, label, chartConfig }: AnonymousChartTooltipProps) {
    if (!active || !payload?.length) {
        return null;
    }

    const ranked = payload
        .map((item) => ({
            name: String(item.name ?? item.dataKey ?? ""),
            value: Number(item.value) || 0,
            color: item.color,
        }))
        .filter((item) => item.value > 0)
        .sort((a, b) => b.value - a.value);

    const top = ranked.slice(0, TOOLTIP_TOP_N);
    const remaining = ranked.length - top.length;
    const periodTotal = ranked.reduce((sum, item) => sum + item.value, 0);

    return (
        <div className="items-start gap-1.5 grid bg-slate-50 dark:bg-slate-950 shadow-xl px-2.5 py-1.5 border border-slate-200 border-slate-200/50 dark:border-slate-800 dark:border-slate-800/50 rounded-lg min-w-[10rem] max-w-[18rem] text-xs">
            <div className="font-medium text-slate-950 dark:text-slate-50">{label}</div>
            <div className="gap-1.5 grid">
                {top.map((item) => (
                    <div key={item.name} className="flex justify-between items-center gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                            <span
                                className="rounded-full w-2 h-2 shrink-0"
                                style={{ backgroundColor: chartConfig[item.name]?.color ?? item.color }}
                            />
                            <FormNameLabel
                                name={item.name}
                                className="text-slate-600 dark:text-slate-400 text-left truncate"
                            />
                        </div>
                        <span className="font-mono font-medium tabular-nums text-slate-950 dark:text-slate-50">
                            {item.value.toLocaleString()}
                        </span>
                    </div>
                ))}
                {remaining > 0 && (
                    <p className="text-slate-500 dark:text-slate-400">
                        + {remaining} more questionnaire{remaining === 1 ? "" : "s"}
                    </p>
                )}
            </div>
            <div className="flex justify-between items-center pt-1 border-slate-200 dark:border-slate-800 border-t font-medium">
                <span>Total</span>
                <span className="font-mono tabular-nums">{periodTotal.toLocaleString()}</span>
            </div>
        </div>
    );
}

function AnonymousSubmissionsTable({ model }: { model: AnonymousChartModel }) {
    const [sorting, setSorting] = useState<SortingState>([{ id: "rowTotal", desc: true }]);
    const tableData = useMemo(() => buildFormTableRows(model), [model]);
    const columns = useMemo(() => buildSubmissionTableColumns(model), [model]);
    const periodTotals = model.chartRows.map((row) =>
        model.formNames.reduce<number>((sum, form) => sum + (Number(row[form]) || 0), 0)
    );

    const table = useReactTable({
        data: tableData,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <div className="border rounded-md overflow-x-auto">
            <Table>
                <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header, index) => {
                                const alignRight =
                                    (header.column.columnDef.meta as { align?: string } | undefined)?.align === "right";
                                return (
                                    <TableHead
                                        key={header.id}
                                        className={
                                            index === 0
                                                ? "left-0 z-10 sticky bg-background min-w-[12rem] whitespace-nowrap"
                                                : alignRight
                                                  ? "text-right whitespace-nowrap"
                                                  : "whitespace-nowrap"
                                        }
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows.map((row) => (
                        <TableRow key={row.id}>
                            {row.getVisibleCells().map((cell, index) => {
                                const alignRight =
                                    (cell.column.columnDef.meta as { align?: string } | undefined)?.align === "right";
                                return (
                                    <TableCell
                                        key={cell.id}
                                        className={
                                            index === 0
                                                ? "left-0 z-10 sticky bg-background whitespace-nowrap"
                                                : alignRight
                                                  ? "text-right"
                                                  : undefined
                                        }
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    ))}
                    <TableRow className="bg-muted/50">
                        <TableCell className="left-0 z-10 sticky bg-muted/50 font-semibold whitespace-nowrap">
                            Total
                        </TableCell>
                        {periodTotals.map((total, index) => (
                            <TableCell
                                key={`total-${model.periods[index]!.key}`}
                                className="font-semibold tabular-nums text-right"
                            >
                                {total > 0 ? total.toLocaleString() : "-"}
                            </TableCell>
                        ))}
                        <TableCell className="font-semibold tabular-nums text-right">
                            {model.totalSubmissions.toLocaleString()}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </div>
    );
}

function AnonymousFormsPage() {
    useNav([
        { label: "Customers", route: { to: "/customers/success" } },
        { label: "Anonymous", route: { to: "/customers/anonymous" } },
    ]);

    const [granularity, setGranularity] = useState<SubmissionPeriodGranularity>("week");

    const { data: submissions, isLoading, error } = useAnonymousFormSubmissions();

    const model = useMemo(() => {
        if (!submissions?.length) {
            return null;
        }
        return buildAnonymousChartModel(submissions, granularity);
    }, [submissions, granularity]);

    const chartConfig = useMemo(() => generateChartConfig(model?.formNames ?? []), [model?.formNames]);

    const meta = GRANULARITY_META[granularity];

    return (
        <div className="flex flex-col gap-4">
            <AdminPageHeader
                title="Anonymous submissions"
                description="Submissions from trackers on PUBLIC_FORM diaries, grouped by form. Empty periods are included so the timeline always runs up to today."
            />
            {isLoading && (
                <div className="flex justify-center items-center py-12">
                    <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
                </div>
            )}

            {error && (
                <ui.Alert variant="destructive">
                    <ui.AlertTitle>Error</ui.AlertTitle>
                    <ui.AlertDescription>
                        Failed to load anonymous form submissions. Please try again later.
                    </ui.AlertDescription>
                </ui.Alert>
            )}

            {!isLoading && !error && (!model || model.totalSubmissions === 0) && (
                <ui.Card>
                    <ui.CardContent className="py-12">
                        <p className="text-muted-foreground text-center">
                            No anonymous form submissions in the current view.
                        </p>
                    </ui.CardContent>
                </ui.Card>
            )}

            {!isLoading && !error && model && model.totalSubmissions > 0 && (
                <TooltipProvider>
                    <div className="space-y-6">
                        <div className="gap-4 grid md:grid-cols-3">
                            <ui.Card>
                                <ui.CardHeader className="flex flex-row justify-between items-center space-y-0 pb-2">
                                    <ui.CardTitle className="font-medium text-sm">Latest period</ui.CardTitle>
                                </ui.CardHeader>
                                <ui.CardContent>
                                    <div className="font-bold text-2xl">{model.currentPeriodTotal}</div>
                                    <p className="text-muted-foreground text-xs">{model.currentPeriodLabel}</p>
                                </ui.CardContent>
                            </ui.Card>

                            <ui.Card>
                                <ui.CardHeader className="flex flex-row justify-between items-center space-y-0 pb-2">
                                    <ui.CardTitle className="font-medium text-sm">Total in view</ui.CardTitle>
                                </ui.CardHeader>
                                <ui.CardContent>
                                    <div className="font-bold text-2xl">{model.totalSubmissions}</div>
                                    <p className="text-muted-foreground text-xs">{meta.rangeDescription}</p>
                                </ui.CardContent>
                            </ui.Card>

                            <ui.Card>
                                <ui.CardHeader className="flex flex-row justify-between items-center space-y-0 pb-2">
                                    <ui.CardTitle className="font-medium text-sm">Change vs previous</ui.CardTitle>
                                </ui.CardHeader>
                                <ui.CardContent>
                                    {model.previousPeriodTotal === null ? (
                                        <div className="flex items-center gap-2 font-bold text-2xl">
                                            <Minus className="w-4 h-4" />
                                            <span>N/A</span>
                                        </div>
                                    ) : (
                                        (() => {
                                            const previousTotal = model.previousPeriodTotal;
                                            const currentTotal = model.currentPeriodTotal;
                                            const diff = previousTotal === null ? 0 : currentTotal - previousTotal;
                                            const percentChange =
                                                previousTotal === null || previousTotal === 0
                                                    ? 0
                                                    : (diff / previousTotal) * 100;
                                            return (
                                                <>
                                                    <div className="flex items-center gap-2 font-bold text-2xl">
                                                        {diff > 0 ? (
                                                            <TrendingUp className="w-4 h-4 text-green-600" />
                                                        ) : diff < 0 ? (
                                                            <TrendingDown className="w-4 h-4 text-red-600" />
                                                        ) : (
                                                            <Minus className="w-4 h-4" />
                                                        )}
                                                        <span
                                                            className={
                                                                diff > 0
                                                                    ? "text-green-600"
                                                                    : diff < 0
                                                                      ? "text-red-600"
                                                                      : ""
                                                            }
                                                        >
                                                            {diff > 0 ? "+" : ""}
                                                            {diff}
                                                        </span>
                                                    </div>
                                                    <p className="text-muted-foreground text-xs">
                                                        {percentChange > 0 ? "+" : ""}
                                                        {percentChange.toFixed(1)}% vs {meta.trendCompareLabel}
                                                    </p>
                                                </>
                                            );
                                        })()
                                    )}
                                </ui.CardContent>
                            </ui.Card>
                        </div>

                        <ui.Card>
                            <ui.CardHeader className="flex sm:flex-row flex-col sm:justify-between sm:items-start gap-4">
                                <div className="space-y-1">
                                    <ui.CardTitle>Submission trends</ui.CardTitle>
                                    <ui.CardDescription>
                                        Stacked area chart by form. Window: {meta.rangeDescription} (oldest left, newest
                                        right).
                                    </ui.CardDescription>
                                </div>
                                <ui.Select
                                    value={granularity}
                                    onValueChange={(v) => setGranularity(v as SubmissionPeriodGranularity)}
                                >
                                    <ui.SelectTrigger className="w-[140px]">
                                        <ui.SelectValue placeholder="Granularity" />
                                    </ui.SelectTrigger>
                                    <ui.SelectContent>
                                        {(Object.keys(GRANULARITY_META) as SubmissionPeriodGranularity[]).map((g) => (
                                            <ui.SelectItem key={g} value={g}>
                                                {GRANULARITY_META[g].selectLabel}
                                            </ui.SelectItem>
                                        ))}
                                    </ui.SelectContent>
                                </ui.Select>
                            </ui.CardHeader>
                            <ui.CardContent>
                                <ChartContainer config={chartConfig} className="w-full h-[480px] min-h-[380px]">
                                    <AreaChart
                                        accessibilityLayer
                                        data={model.chartRows}
                                        margin={{ left: 8, right: 8, top: 8, bottom: granularity === "day" ? 48 : 28 }}
                                    >
                                        <CartesianGrid vertical={false} />
                                        <XAxis
                                            dataKey="periodLabel"
                                            tickLine={false}
                                            axisLine={false}
                                            tickMargin={8}
                                            minTickGap={granularity === "day" ? 16 : 8}
                                            tick={{ fontSize: 11 }}
                                            angle={granularity === "day" ? -38 : -20}
                                            textAnchor="end"
                                            height={granularity === "day" ? 72 : 52}
                                        />
                                        <YAxis tickLine={false} axisLine={false} tickMargin={8} width={40} />
                                        <ChartTooltip
                                            content={(props) => (
                                                <AnonymousChartTooltip
                                                    active={props.active}
                                                    payload={
                                                        props.payload as AnonymousChartTooltipPayloadItem[] | undefined
                                                    }
                                                    label={props.label}
                                                    chartConfig={chartConfig}
                                                />
                                            )}
                                        />
                                        {model.formNames.map((formName, index) => {
                                            const color =
                                                chartConfig[formName]?.color || FORM_COLORS[index % FORM_COLORS.length];
                                            return (
                                                <Area
                                                    key={formName}
                                                    type="monotone"
                                                    dataKey={formName}
                                                    stackId="anonymous-submissions"
                                                    stroke={color}
                                                    fill={color}
                                                    fillOpacity={0.65}
                                                    strokeWidth={1.5}
                                                />
                                            );
                                        })}
                                    </AreaChart>
                                </ChartContainer>
                            </ui.CardContent>
                            <ui.CardFooter className="flex-col items-start gap-2 text-sm">
                                <div className="text-muted-foreground leading-none">
                                    Tooltip shows the top {TOOLTIP_TOP_N} questionnaires for that period. Periods with
                                    no submissions show as zero through to today.
                                </div>
                            </ui.CardFooter>
                        </ui.Card>

                        <ui.Card>
                            <ui.CardHeader>
                                <ui.CardTitle>Full breakdown</ui.CardTitle>
                                <ui.CardDescription>
                                    Submission counts for every questionnaire in the chart window (
                                    {meta.rangeDescription}
                                    ).
                                </ui.CardDescription>
                            </ui.CardHeader>
                            <ui.CardContent>
                                <AnonymousSubmissionsTable model={model} />
                            </ui.CardContent>
                        </ui.Card>
                    </div>
                </TooltipProvider>
            )}
        </div>
    );
}
